import React from "react";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Kereshető lenyíló lista (Combobox).
 * - value: string (jelenlegi érték)
 * - onChange: (val) => void
 * - options: string[]
 * - loading: boolean — töltés jelző
 * - placeholder: string — üres állapot label
 * - disabled: boolean
 * - testid: string — data-testid a triggerre
 * - emptyText: string — nincs találat szöveg
 * - searchable: boolean — keresőmező (false = sima legördülő, mint natív select)
 */
export const SearchableSelect = ({
  value,
  onChange,
  options = [],
  loading = false,
  placeholder = "Válassz...",
  disabled = false,
  testid,
  emptyText = "Nincs találat",
  searchable = true,
}) => {
  const [open, setOpen] = React.useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          disabled={disabled || loading}
          className={cn(
            "w-full h-11 flex items-center justify-between gap-2 rounded-xl px-3 text-sm text-left",
            "bg-white/[0.03] border border-white/10 hover:border-white/20 focus:border-electric outline-none transition-colors",
            "disabled:opacity-50 disabled:cursor-not-allowed"
          )}
        >
          <span className={cn("truncate", !value && "text-slate-500")}>
            {loading ? "Betöltés..." : value || placeholder}
          </span>
          {loading ? (
            <Loader2 className="w-4 h-4 shrink-0 animate-spin text-slate-400" />
          ) : (
            <ChevronsUpDown className="w-4 h-4 shrink-0 opacity-50" />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 bg-anthracite-700 border-white/10 text-white w-[--radix-popover-trigger-width]"
        align="start"
      >
        <Command className="bg-transparent" shouldFilter={searchable}>
          {searchable && (
            <CommandInput
              placeholder="Keresés..."
              className="text-white placeholder:text-slate-500"
              data-testid={testid ? `${testid}-search` : undefined}
            />
          )}
          <CommandList className={searchable ? undefined : "max-h-[min(50vh,280px)] overflow-y-auto"}>
            <CommandEmpty className="py-6 text-center text-sm text-slate-400">
              {emptyText}
            </CommandEmpty>
            <CommandGroup>
              {options.map((opt) => (
                <CommandItem
                  key={opt}
                  value={opt}
                  data-testid={testid ? `${testid}-option-${opt}` : undefined}
                  onSelect={(selected) => {
                    // cmdk lowercases; find the original value case-insensitively
                    const match = options.find((o) => o.toLowerCase() === selected.toLowerCase()) || opt;
                    onChange(match);
                    setOpen(false);
                  }}
                  className="text-white aria-selected:bg-electric aria-selected:text-white cursor-pointer"
                >
                  <Check className={cn("mr-2 h-4 w-4", value === opt ? "opacity-100" : "opacity-0")} />
                  {opt}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
